import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Search,
  Loader2,
  AppWindow,
  Users,
  Lock,
  Filter,
  Play,
  Star,
} from 'lucide-react'
import { listAllApps, startApp, stopApp, rebuildApp, toggleFeaturedApp, type AdminApp } from '../../lib/api'
import AppActions from '../../components/AppActions'
type AdminAppSort = 'recent' | 'most-viewed' | 'name'

const statusConfig = {
  running: { color: 'bg-green-500', label: 'Running' },
  stopped: { color: 'bg-gray-500', label: 'Stopped' },
  starting: { color: 'bg-amber-500', label: 'Starting' },
  error: { color: 'bg-red-500', label: 'Error' },
}

export default function AdminApps() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [apps, setApps] = useState<AdminApp[]>([])
  const [loading, setLoading] = useState(true)
  const search = searchParams.get('query') ?? ''
  const statusFilter = searchParams.get('status') ?? 'all'
  const rawSort = searchParams.get('sort')
  const sortBy: AdminAppSort = rawSort === 'most-viewed' || rawSort === 'name' ? rawSort : 'recent'

  const loadApps = useCallback(async () => {
    try {
      const data = await listAllApps()
      setApps(data)
    } catch (err) {
      console.error('Failed to load apps:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadApps()
  }, [loadApps])

  async function handleStart(appName: string) {
    try {
      await startApp(appName)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start app')
    }
    await loadApps()
  }

  async function handleStop(appName: string) {
    try {
      await stopApp(appName)
    } catch (err) {
      console.error('Failed to stop app:', err)
    }
    await loadApps()
  }

  async function handleRebuild(appName: string) {
    try {
      await rebuildApp(appName)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to rebuild app')
    }
    await loadApps()
  }

  async function handleToggleFeatured(appName: string) {
    try {
      await toggleFeaturedApp(appName)
    } catch (err) {
      console.error('Failed to toggle featured:', err)
    }
    await loadApps()
  }

  function updateFilters(nextSearch: string, nextStatus: string, nextSort: AdminAppSort) {
    const nextParams = new URLSearchParams(searchParams)
    const trimmedSearch = nextSearch.trim()

    if (trimmedSearch) {
      nextParams.set('query', trimmedSearch)
    } else {
      nextParams.delete('query')
    }

    if (nextStatus !== 'all') {
      nextParams.set('status', nextStatus)
    } else {
      nextParams.delete('status')
    }

    if (nextSort !== 'recent') {
      nextParams.set('sort', nextSort)
    } else {
      nextParams.delete('sort')
    }

    setSearchParams(nextParams, { replace: true })
  }

  const filteredApps = apps
    .filter(app => {
      const matchesSearch = search === '' ||
        app.name.toLowerCase().includes(search.toLowerCase()) ||
        app.user_email.toLowerCase().includes(search.toLowerCase()) ||
        (app.user_name && app.user_name.toLowerCase().includes(search.toLowerCase()))
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter
      return matchesSearch && matchesStatus
    })
    .sort((left, right) => {
      if (sortBy === 'most-viewed') {
        return (
          (right.visit_count_30d ?? 0) - (left.visit_count_30d ?? 0)
          || (right.unique_visitors_30d ?? 0) - (left.unique_visitors_30d ?? 0)
          || (left.display_name || left.name).localeCompare(right.display_name || right.name)
        )
      }

      if (sortBy === 'name') {
        return (left.display_name || left.name).localeCompare(right.display_name || right.name)
      }

      return (
        new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
        || (left.display_name || left.name).localeCompare(right.display_name || right.name)
      )
    })

  const stats = {
    total: apps.length,
    running: apps.filter(a => a.status === 'running').length,
    featured: apps.filter(a => a.is_featured).length,
    byAccess: {
      personal: apps.filter(a => a.access_level === 'personal').length,
      shared: apps.filter(a => a.access_level !== 'personal').length,
    },
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">All Apps</h1>
          <p className="text-muted-foreground">Manage deployed apps across all users</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <AppWindow className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Apps</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Play className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.running}</p>
              <p className="text-xs text-muted-foreground">Running</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.featured}</p>
              <p className="text-xs text-muted-foreground">Featured</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.byAccess.personal}</p>
              <p className="text-xs text-muted-foreground">Personal</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.byAccess.shared}</p>
              <p className="text-xs text-muted-foreground">Shared</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => updateFilters(e.target.value, statusFilter, sortBy)}
            placeholder="Search by app name or user..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => updateFilters(search, e.target.value, sortBy)}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="running">Running</option>
            <option value="stopped">Stopped</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort</span>
          <select
            value={sortBy}
            onChange={(e) => updateFilters(search, statusFilter, e.target.value as AdminAppSort)}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="recent">Newest</option>
            <option value="most-viewed">Most Viewed (30d)</option>
            <option value="name">Name A-Z</option>
          </select>
        </div>
      </div>

      {/* Apps Table */}
      {filteredApps.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {search || statusFilter !== 'all' ? 'No apps match your filters' : 'No apps have been created yet'}
        </div>
      ) : (
        <>
        {/* Mobile: card list (md:hidden). Stacked layout — no horizontal scroll. */}
        <div className="md:hidden space-y-3">
          {filteredApps.map((app) => {
            const status = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.stopped
            const isLiveOnly = app.inventory_source === 'live_only'
            const visitCount = app.visit_count_30d ?? 0
            const uniqueVisitors = app.unique_visitors_30d ?? 0
            return (
              <div
                key={`m-${app.id}`}
                className="border border-border rounded-xl p-4 bg-card cursor-pointer hover:bg-secondary/30"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('button, a')) return
                  navigate(`/admin/apps/${encodeURIComponent(app.name)}`)
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{app.display_name || app.name}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate">/a/{app.name}/</p>
                  </div>
                  <button
                    title={isLiveOnly ? 'Unavailable for live-only inventory rows' : app.is_featured ? 'Remove from featured' : 'Add to featured'}
                    onClick={(e) => { e.stopPropagation(); handleToggleFeatured(app.name) }}
                    disabled={isLiveOnly}
                    className={`shrink-0 p-1.5 rounded-lg transition-colors ${
                      app.is_featured
                        ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                        : 'text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/5'
                    } disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    <Star className="w-4 h-4" fill={app.is_featured ? 'currentColor' : 'none'} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${status.color}`} />
                  <span className="text-sm">{status.label}</span>
                  {app.live_health && app.live_health !== 'none' && (
                    <span className="text-xs text-muted-foreground">({app.live_health})</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <span className="truncate">
                    {app.user_id > 0 ? (
                      <Link
                        to={`/admin/users/${app.user_id}`}
                        className="hover:text-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {app.user_name || app.user_email}
                      </Link>
                    ) : (
                      <span>{app.user_name || app.user_email}</span>
                    )}
                  </span>
                  <span className="shrink-0 ml-2">
                    {visitCount.toLocaleString()} views · {uniqueVisitors.toLocaleString()} unique
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {new Date(app.created_at).toLocaleDateString()}
                  </span>
                  {isLiveOnly ? (
                    <span className="text-xs text-muted-foreground">No DB record</span>
                  ) : (
                    <AppActions
                      app={app}
                      onStart={async () => handleStart(app.name)}
                      onStop={async () => handleStop(app.name)}
                      onRebuild={async () => handleRebuild(app.name)}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Desktop: table (hidden on mobile to avoid horizontal scroll on 375px). */}
        <div className="hidden md:block border border-border rounded-xl overflow-x-auto">
          <table className="w-full min-w-[940px]">
            <thead className="bg-secondary/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">App</th>
                <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">Owner</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium whitespace-nowrap">Views (30d)</th>
                <th className="px-4 py-3 text-center text-sm font-medium">Featured</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Created</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredApps.map((app) => {
                const status = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.stopped
                const isLiveOnly = app.inventory_source === 'live_only'
                const isMerged = app.inventory_source === 'merged'
                const visitCount = app.visit_count_30d ?? 0
                const uniqueVisitors = app.unique_visitors_30d ?? 0

                return (
                  <tr
                    key={app.id}
                    className="hover:bg-secondary/30 cursor-pointer"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, a')) return
                      navigate(`/admin/apps/${encodeURIComponent(app.name)}`)
                    }}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{app.display_name || app.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">/a/{app.name}/</p>
                        {isLiveOnly && (
                          <p className="mt-1 text-xs text-amber-600">
                            Live inventory only{app.live_container_count ? ` · ${app.live_container_count} containers` : ''}
                          </p>
                        )}
                        {isMerged && app.live_container_count ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Live state merged · {app.live_container_count} containers
                          </p>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {app.user_id > 0 ? (
                        <Link
                          to={`/admin/users/${app.user_id}`}
                          className="text-sm hover:text-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {app.user_name || app.user_email}
                        </Link>
                      ) : (
                        <span className="text-sm text-muted-foreground">{app.user_name || app.user_email}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${status.color}`} />
                        <span className="text-sm">{status.label}</span>
                        {app.live_health && app.live_health !== 'none' && (
                          <span className="text-xs text-muted-foreground">({app.live_health})</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>
                        <p className="text-sm font-medium">{visitCount.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">{uniqueVisitors.toLocaleString()} unique</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      
                        <button title={isLiveOnly ? 'Unavailable for live-only inventory rows' : app.is_featured ? "Remove from featured apps" : "Add to featured apps list"}
                          onClick={(e) => { e.stopPropagation(); handleToggleFeatured(app.name) }}
                          disabled={isLiveOnly}
                          className={`p-1.5 rounded-lg transition-colors ${
                            app.is_featured
                              ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                              : 'text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/5'
                          } disabled:cursor-not-allowed disabled:opacity-40`}
                        >
                          <Star className="w-4 h-4" fill={app.is_featured ? 'currentColor' : 'none'} />
                        </button>
                      
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        {isLiveOnly ? (
                          <span className="text-xs text-muted-foreground">No DB record</span>
                        ) : (
                          <AppActions
                            app={app}
                            onStart={async () => handleStart(app.name)}
                            onStop={async () => handleStop(app.name)}
                            onRebuild={async () => handleRebuild(app.name)}
                            size="sm"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  )
}
